import { useState } from "react";

import { Box, Grid, Typography, IconButton, Tooltip } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

import { TextField } from "@aclymatepackages/atoms";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { truncateText } from "@aclymatepackages/other-helpers";

import GrayPaper from "../../atoms/GrayPaper";
import FileUploadDropzone from "../../atoms/FileUploadDropzone";
import DocumentationDownloadButton from "../../atoms/buttons/DocumentationDownloadButton";
import DocumentationYear from "../../inputs/tags/DocumentationYear";
import DocumentationCustomTags from "../../inputs/tags/DocumentationCustomTags";

import {
  useCachedDisplayData,
  uploadFileToStorage,
  useCachedFirebaseCrud,
  deleteFileFromStorage,
  useAuth
} from "../../../helpers/firebase";
import { getAccountId } from "../../../helpers/accountHelpers";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";

const MetadataInputs = ({
  newCustomTag,
  setNewCustomTag,
  fileData,
  setFileData
}) => {
  const { name, description, yearTag, customTags = [] } = fileData;

  const editFileData = (field) => (value) =>
    editObjectData(setFileData, field, value);

  return (
    <>
      <Grid item>
        <TextField
          value={name}
          setValue={editFileData("name")}
          label="File Name"
        />
      </Grid>
      <Grid item>
        <TextField
          value={description}
          setValue={editFileData("description")}
          label="File Description (optional)"
          multiline
          fullWidth
          minRows={3}
        />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item sx={6}>
          <DocumentationYear
            yearTag={yearTag}
            editYearTag={editFileData("yearTag")}
          />
        </Grid>
        <Grid item sx={6}>
          <DocumentationCustomTags
            customTags={customTags}
            editCustomTags={editFileData("customTags")}
            newTag={newCustomTag}
            setNewTag={setNewCustomTag}
          />
        </Grid>
      </Grid>
    </>
  );
};

const DocumentationCard = ({ name, url, path, description, id }) => {
  const [editMetadata, setEditMetadata] = useState(false);
  const [editableMetadata, setEditableMetadata] = useState({
    name,
    description
  });

  const { deleteCachedCollectionDoc, updateCollectionDoc } =
    useCachedFirebaseCrud();
  const { canEdit } = usePermissions();

  const [originalName] = path.split("/").reverse();

  const onDeleteDocumentationFile = () => {
    deleteCachedCollectionDoc("documentation-files", id);
    return deleteFileFromStorage(path);
  };

  const actionButtons = editMetadata
    ? [
        {
          tooltip: "Save Edits",
          icon: <SaveIcon />,
          onClick: () => {
            updateCollectionDoc("documentation-files", id, editableMetadata);
            return setEditMetadata(false);
          },
          disabled: !canEdit
        },
        {
          tooltip: "Cancel Edits",
          icon: <CancelIcon />,
          onClick: () => {
            setEditableMetadata({ name, description });
            setEditMetadata(false);
          }
        }
      ]
    : [
        {
          icon: <EditIcon />,
          onClick: () => setEditMetadata(true),
          tooltip: "Edit File Metadata",
          disabled: !canEdit
        },
        {
          tooltip: "Delete File",
          icon: <DeleteIcon />,
          onClick: onDeleteDocumentationFile,
          disabled: !canEdit
        }
      ];

  return (
    <Grid item>
      <GrayPaper style={{ width: "100%" }}>
        <Grid container spacing={2} direction="column">
          <Grid
            item
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              {editMetadata ? (
                <Typography variant="h5">
                  Edit Metadata for {originalName}
                </Typography>
              ) : (
                <>
                  <Typography variant="h5">
                    {name}
                    {!!description && (
                      <span>
                        {" "}
                        <Tooltip title={description}>
                          <FontAwesomeIcon icon={faQuestionCircle} />
                        </Tooltip>
                      </span>
                    )}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {truncateText(originalName, 40)}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid item>
              <Grid container>
                {actionButtons.map(
                  ({ icon, onClick, tooltip, disabled }, idx) => (
                    <Grid item key={`doc-action-button-${idx}`}>
                      <Tooltip title={tooltip}>
                        <span>
                          <IconButton onClick={onClick} disabled={disabled}>
                            {icon}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  )
                )}
                {!editMetadata && (
                  <Grid item>
                    <DocumentationDownloadButton name={name} url={url} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          {editMetadata && (
            <MetadataInputs
              fileData={editableMetadata}
              setFileData={setEditableMetadata}
            />
          )}
        </Grid>
      </GrayPaper>
    </Grid>
  );
};

const DocumentationUploader = ({
  type,
  docId,
  docName,
  showDocumentationFiles = true,
  onUpload
}) => {
  const { isSimulation } = usePlatformLayoutContext() || {};
  const { canEdit } = usePermissions();

  const queries =
    docId && type
      ? [
          ["linkedDoc.id", "==", docId],
          ["type", "==", type]
        ]
      : [];

  const [user] = useAuth();
  const { displayName, uid } = user || {};
  const [documentationFiles] = useCachedDisplayData(
    "documentation-files",
    queries
  );

  const { newCollectionDoc } = useCachedFirebaseCrud();

  const [error, setError] = useState(null);
  const [reset, setReset] = useState(false);
  const [documentationFile, setDocumentationFile] = useState(null);
  const { name: docFileName } = documentationFile || {};

  const [newCustomTag, setNewCustomTag] = useState("");
  const [fileData, setFileData] = useState({});
  const { name: fileName } = fileData || {};

  const onDocumentationFileUpload = async () => {
    const path = `${getAccountId()}/documentation/${type}/${docId}/${docFileName}`;

    const docUrl = await uploadFileToStorage({
      path,
      fileObject: documentationFile
    });
    const { isDuplicateFileDetected: isDuplicateFileDetectedFromUpload } =
      docUrl || {};

    if (isDuplicateFileDetectedFromUpload) {
      return setError(
        `This file has already been uploaded. Please try uploading a different file.`
      );
    }

    setError(null);

    newCollectionDoc("documentation-files", {
      type,
      linkedDoc: { id: docId, name: docName },
      dateCreated: new Date(),
      url: docUrl,
      path,
      uploadUser: { name: displayName, uid },
      ...fileData,
      customTags: newCustomTag ? [newCustomTag] : []
    });

    if (onUpload) {
      return onUpload();
    }

    setFileData({});
    setReset(true);
    return setDocumentationFile(null);
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <GrayPaper>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <Box style={{ height: 150, width: "100%" }}>
                <FileUploadDropzone
                  fileName="documentation"
                  setFile={(file) => {
                    const { name: fileName } = file || {};
                    setDocumentationFile(file);
                    setFileData({ name: fileName });
                    return setError(null);
                  }}
                  reset={reset}
                  setReset={setReset}
                  disabled={isSimulation || !canEdit}
                />
              </Box>
              {error && (
                <Typography
                  color="error"
                  variant="caption"
                  align="center"
                  display="block"
                >
                  {error}
                </Typography>
              )}
            </Grid>
            {documentationFile && (
              <>
                <MetadataInputs
                  newCustomTag={newCustomTag}
                  setNewCustomTag={setNewCustomTag}
                  fileData={fileData}
                  setFileData={setFileData}
                />
                <Grid item container justifyContent="center">
                  <Grid item>
                    <Tooltip title="Upload Documentation File">
                      <span>
                        <IconButton
                          disabled={!fileName || !canEdit}
                          onClick={onDocumentationFileUpload}
                        >
                          <CloudUploadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Cancel Upload">
                      <IconButton
                        onClick={() => {
                          setDocumentationFile(null);
                          setReset(true);
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </GrayPaper>
      </Grid>
      {showDocumentationFiles &&
        documentationFiles.map((file, idx) => (
          <DocumentationCard {...file} key={`doc-file-${idx}`} />
        ))}
    </Grid>
  );
};
export default DocumentationUploader;
